// ============================================================================
// FSA Engagement — Corporate Facts pane (ADR-0024 §2)
// ============================================================================
// Secretarial facts for the engagement year, entered ONCE and read by every
// statutory document — the Board's Report now; the annexures, AGM Notice,
// MGT-11 and attendance slip in later slices. Typed separately into each
// document these facts drift; here each is entered in one place.
//
// Cards:
//   • Annual General Meeting — number, date, time, venue (columns)
//   • Board Meetings        — dated list
//   • Attendance            — directors × meetings grid. Ticks reference
//                             meetings by id, so attendance can only be
//                             recorded against a listed meeting; a director
//                             not in office on a meeting date cannot be ticked
//   • Changes in Directors  — appointments / cessations with dates and DINs
//   • Applicability         — the Board's Report questions (read from the
//                             pinned report component), each driving the
//                             "applies" / "not applicable" text of a section
//   • Dividends · POSH · Employees
//
// Directors (ADR-0024): the engagement keeps its OWN list (`directors`),
// taken from the director signatory master and then owned here — so a later
// master edit cannot change an old year. Its order is the order the Board's
// Report, attendance, the pickers and new signature panels list them in.
// Master changes show as a notice with "Sync from master"; never automatic.
//
// Judgement calls (a meeting outside the year, an unanswered question) show
// as warnings; only structural integrity blocks a save (server-validated).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { useFsaCorporateFacts, parseCorporateFactsData } from 'hooks/api/fsa/useFsaCorporateFacts';
import { fsaCorporateFactsService } from 'api/services/fsa/fsaCorporateFactsService';
import { fsaReportService } from 'api/services/fsa/fsaReportService';
import { generateLocalId } from 'components/modals/modules/fsa/notes/panes/policy/SectionedNarrativeEditor';
import {
  collectQuestions,
  derivedFromFacts,
  parseSectionDefinitions,
  type ApplicabilityQuestion,
} from 'components/modals/modules/fsa/reports/reportSections';
import RelatedPartyRegisterEditor from './RelatedPartyRegisterEditor';
import { fromMaster, hasDrift, inOffice, moveEntry, overlaps, rosterDrift, syncRoster } from './factsDirectors';
import { changedSections, sectionSaveRequest } from './factsSections';
import MembersEditor from './MembersEditor';
import { closingMembers } from './factsMembers';
import { NoteGridsEditor, forexFromGrids } from './NoteGridsEditor';
import { fsaNotesService } from 'api/services/fsa/fsaNotesService';
import type { ReportLiveData, ReportSectionDefinition } from 'api/types/fsa/reportData.types';
import type {
  BoardMeeting,
  CorporateFactsData,
  CorporateFactsDirector,
  DirectorChange,
  DirectorChangeKind,
  DividendEntry,
  MasterDirectorEntry,
  RelatedPartyImportResult,
  RelatedPartyTransaction,
} from 'api/types/fsa/corporateFacts.types';
import type { ApplicabilitySuggestion } from 'api/types/fsa/reportData.types';

/** The workspace's sections, in menu order. */
export type FactsSection = 'company' | 'directors' | 'meetings' | 'changes' | 'members' | 'questions' | 'related' | 'additional' | 'dividends' | 'other';

/** The data keys each section edits (its unsaved dot and its share of a section save). */
const SECTION_KEYS: Record<FactsSection, string[]> = {
  company: ['nature_of_business'],
  directors: ['directors'],
  meetings: ['board_meetings', 'attendance'],
  changes: ['director_changes'],
  members: ['share_classes', 'members_opening', 'share_movements'],
  questions: ['applicability', 'applicability_carried'],
  related: ['related_parties', 'related_party_transactions'],
  additional: ['note_grids', 'ratio_py_openings'],
  dividends: ['dividends'],
  other: ['forex', 'posh', 'employees'],
};
const SECTION_PREF_KEY = 'fsa.corporateFacts.section';
const ADDITIONAL_INFORMATION_SCHEDULE = 'NOTE_ADDITIONAL_INFORMATION';

interface Props {
  engagementId: string;
  clientId?: string | null;
  /** Open on this section (e.g. from a note's "Edit data"); else the last one used. */
  initialSection?: FactsSection;
  /** Told when unsaved changes appear / go (a host sheet asks before closing). */
  onDirtyChange?: (dirty: boolean) => void;
  /** Told after every successful save (a host note refreshes its tables). */
  onSaved?: () => void;
  /** ISO date — FY start (derived from FY end when absent). */
  periodStart?: string | null;
  /** ISO date — FY end. */
  financialYearEnd: string;
}

/** The narrative component whose questionnaire this pane answers. */
const BOARDS_REPORT_CODE = 'DIRECTORS_REPORT_SCH3';

const CHANGE_KIND_LABEL: Record<DirectorChangeKind, string> = {
  APPOINTED: 'Appointed',
  ADDITIONAL_DIRECTOR: 'Appointed as Additional Director',
  REGULARISED: 'Regularised at AGM',
  REAPPOINTED: 'Re-appointed',
  CEASED: 'Ceased',
};

interface Draft {
  agmNumber: string;
  agmDate: string;
  agmTime: string;
  agmVenue: string;
  data: CorporateFactsData;
}

const isoDate = (s: string | null | undefined): string => (s ? s.slice(0, 10) : '');

function fyStartFrom(periodStart: string | null | undefined, fyEnd: string): string {
  if (periodStart) return isoDate(periodStart);
  const end = new Date(isoDate(fyEnd) + 'T00:00:00Z');
  const start = new Date(Date.UTC(end.getUTCFullYear() - 1, end.getUTCMonth(), end.getUTCDate() + 1));
  return start.toISOString().slice(0, 10);
}

function formatDisplayDate(iso: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function toNumberOrNull(v: string): number | null {
  if (v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const FsaCorporateFactsPane: React.FC<Props> = ({ engagementId, clientId, periodStart, financialYearEnd, initialSection, onDirtyChange, onSaved }) => {
  const { data: facts, loading, refetch } = useFsaCorporateFacts(engagementId);
  const fyStart = useMemo(() => fyStartFrom(periodStart, financialYearEnd), [periodStart, financialYearEnd]);
  const fyEnd = isoDate(financialYearEnd);

  // ───── draft state ─────────────────────────────────────────────────────
  const [draft, setDraft] = useState<Draft | null>(null);
  const [snapshot, setSnapshot] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!facts) return;
    const next: Draft = {
      agmNumber: facts.agmNumber != null ? String(facts.agmNumber) : '',
      agmDate: isoDate(facts.agmDate),
      agmTime: facts.agmTime ?? '',
      agmVenue: facts.agmVenue ?? '',
      data: parseCorporateFactsData(facts.data),
    };
    setDraft(next);
    setSnapshot(JSON.stringify(next));
  }, [facts]);

  const isDirty = draft != null && JSON.stringify(draft) !== snapshot;
  useEffect(() => { onDirtyChange?.(isDirty); }, [isDirty, onDirtyChange]);
  const snapshotDraft = useMemo<Draft | null>(() => { try { return snapshot ? JSON.parse(snapshot) : null; } catch { return null; } }, [snapshot]);

  // ───── the section on screen ────────────────────────────────────────────
  const [section, setSection] = useState<FactsSection>(() => {
    if (initialSection) return initialSection;
    try { return (localStorage.getItem(SECTION_PREF_KEY) as FactsSection) || 'company'; } catch { return 'company'; }
  });
  useEffect(() => { if (initialSection) setSection(initialSection); }, [initialSection]);
  const chooseSection = (next: FactsSection) => {
    setSection(next);
    try { localStorage.setItem(SECTION_PREF_KEY, next); } catch { /* storage unavailable */ }
  };
  const handleDiscard = () => {
    if (!snapshot) return;
    setDraft(JSON.parse(snapshot));
  };

  // ───── Additional information — the note's grid definitions + current figures
  const [addlInfo, setAddlInfo] = useState<{ defs: ReportSectionDefinition[]; live: ReportLiveData | null } | null>(null);
  useEffect(() => {
    if (section !== 'additional') return;
    let cancelled = false;
    (async () => {
      try {
        const list = await fsaNotesService.list(engagementId);
        const item = (list.data ?? []).find(n => n.scheduleCode === ADDITIONAL_INFORMATION_SCHEDULE);
        const h = item ? (await fsaNotesService.hydrate(engagementId, item.scheduleId)).data : null;
        if (!cancelled) setAddlInfo({ defs: parseSectionDefinitions(h?.sectionsJson), live: h?.liveData ?? null });
      } catch {
        if (!cancelled) setAddlInfo({ defs: [], live: null });
      }
    })();
    return () => { cancelled = true; };
  }, [section, engagementId, facts?.rowVersion]);
  const [refreshingOpenings, setRefreshingOpenings] = useState(false);
  const handleRefreshOpenings = async () => {
    setRefreshingOpenings(true);
    try {
      const r = await fsaCorporateFactsService.refreshRatioOpenings(engagementId);
      notificationService.toast.success(r.data?.message ?? 'Opening balances taken from the previous year.');
      await refetch();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not take the opening balances from the previous year.');
    } finally {
      setRefreshingOpenings(false);
    }
  };

  const setData = useCallback((patch: Partial<CorporateFactsData>) => {
    setDraft(prev => (prev ? { ...prev, data: { ...prev.data, ...patch } } : prev));
  }, []);

  // ───── directors — the engagement's own list ────────────────────────────
  // The master's directors for the year (up to the report date) — used to
  // seed the list the first time and to show / apply "Sync from master".
  const [master, setMaster] = useState<MasterDirectorEntry[] | null>(null);
  useEffect(() => {
    if (!clientId) { setMaster([]); return; }
    let cancelled = false;
    fsaCorporateFactsService.getMasterDirectors(engagementId)
      .then(list => { if (!cancelled) setMaster(list); })
      .catch(() => { if (!cancelled) setMaster(null); });
    return () => { cancelled = true; };
  }, [clientId, engagementId, facts?.rowVersion]);

  // First open: no list saved yet → take it from the master, AFTER the
  // snapshot, so the pane reads as unsaved until the auditor saves it.
  const [seededFromMaster, setSeededFromMaster] = useState(false);
  useEffect(() => { setSeededFromMaster(false); }, [facts]);
  useEffect(() => {
    if (!draft || master == null || draft.data.directors !== undefined) return;
    setSeededFromMaster(true);
    setDraft(prev => (prev && prev.data.directors === undefined
      ? { ...prev, data: { ...prev.data, directors: master.map(fromMaster) } }
      : prev));
  }, [draft, master]);

  const roster: CorporateFactsDirector[] = useMemo(() => draft?.data.directors ?? [], [draft?.data.directors]);
  // Attendance lists everyone in office at any point in the year, in the list's order.
  const directors = useMemo(() => roster.filter(d => overlaps(d, fyStart, fyEnd)), [roster, fyStart, fyEnd]);
  const drift = useMemo(() => (master && !seededFromMaster ? rosterDrift(roster, master) : null), [roster, master, seededFromMaster]);
  const missingIds = useMemo(() => new Set((drift?.missing ?? []).map(d => d.director_id.toLowerCase())), [drift]);

  const setRoster = (next: CorporateFactsDirector[]) => setData({ directors: next });
  const moveDirector = (index: number, delta: -1 | 1) => setRoster(moveEntry(roster, index, delta));
  const removeDirector = (id: string) => setData({
    directors: roster.filter(d => d.director_id !== id),
    // Ticks for a director no longer listed would be orphaned — drop them too.
    attendance: (draft?.data.attendance ?? []).filter(a => a.director_id.toLowerCase() !== id.toLowerCase()),
  });
  const syncFromMaster = () => {
    if (!master) return;
    setRoster(syncRoster(roster, master));
    notificationService.toast.success('Directors updated from the master — review the order and Save.');
  };

  // ───── Board's Report questionnaire ────────────────────────────────────
  const [questions, setQuestions] = useState<ApplicabilityQuestion[] | null>(null);
  // Suggested answers evaluated server-side from saved statements (e.g. CSR
  // thresholds). Shown beside the question; never applied automatically.
  const [suggestions, setSuggestions] = useState<Record<string, ApplicabilitySuggestion>>({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fsaReportService.list(engagementId);
        const row = (list.data ?? []).find(r => r.narrativeCode === BOARDS_REPORT_CODE && r.contentFormat === 'SECTIONS');
        if (!row) { if (!cancelled) setQuestions([]); return; }
        const h = await fsaReportService.hydrate(engagementId, row.reportId);
        if (!cancelled) {
          // Audit-scope questions (ADR-0024 A-1) are answered in the report
          // editor, not here; this pane answers the company's own facts.
          setQuestions(collectQuestions(parseSectionDefinitions(h.data?.sectionsJson))
            .filter(q => q.scope == null && q.options == null));
          setSuggestions(h.data?.liveData?.suggestions ?? {});
        }
      } catch {
        if (!cancelled) setQuestions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [engagementId]);

  // ───── derived ─────────────────────────────────────────────────────────
  const meetings = useMemo(
    () => [...(draft?.data.board_meetings ?? [])].sort((a, b) => a.date.localeCompare(b.date)),
    [draft?.data.board_meetings],
  );
  const attendanceSet = useMemo(
    () => new Set((draft?.data.attendance ?? []).map(a => `${a.meeting_id}|${a.director_id}`)),
    [draft?.data.attendance],
  );

  // ───── mutators ────────────────────────────────────────────────────────
  const addMeeting = () => setData({
    board_meetings: [...(draft?.data.board_meetings ?? []), { local_id: generateLocalId(), date: '' }],
  });
  const updateMeeting = (id: string, patch: Partial<BoardMeeting>) => setData({
    board_meetings: (draft?.data.board_meetings ?? []).map(m => (m.local_id === id ? { ...m, ...patch } : m)),
  });
  const removeMeeting = (id: string) => setData({
    board_meetings: (draft?.data.board_meetings ?? []).filter(m => m.local_id !== id),
    // A tick against a deleted meeting would be orphaned — drop it with the meeting.
    attendance: (draft?.data.attendance ?? []).filter(a => a.meeting_id !== id),
  });

  const toggleAttendance = (meetingId: string, directorId: string) => {
    const key = `${meetingId}|${directorId}`;
    const current = draft?.data.attendance ?? [];
    setData({
      attendance: attendanceSet.has(key)
        ? current.filter(a => !(a.meeting_id === meetingId && a.director_id === directorId))
        : [...current, { meeting_id: meetingId, director_id: directorId }],
    });
  };
  const markAllPresent = (meeting: BoardMeeting) => {
    const current = draft?.data.attendance ?? [];
    const additions = directors
      .filter(d => inOffice(d, meeting.date) && !attendanceSet.has(`${meeting.local_id}|${d.director_id}`))
      .map(d => ({ meeting_id: meeting.local_id, director_id: d.director_id }));
    setData({ attendance: [...current, ...additions] });
  };

  const addChange = () => setData({
    director_changes: [...(draft?.data.director_changes ?? []), { local_id: generateLocalId(), kind: 'APPOINTED', name: '' }],
  });
  const updateChange = (id: string, patch: Partial<DirectorChange>) => setData({
    director_changes: (draft?.data.director_changes ?? []).map(c => (c.local_id === id ? { ...c, ...patch } : c)),
  });
  const removeChange = (id: string) => setData({
    director_changes: (draft?.data.director_changes ?? []).filter(c => c.local_id !== id),
  });

  // ADR-0024 R-4b — answering (or clearing) a carried question is a decision
  // for THIS year, so it leaves the "carried — confirm" list.
  const withoutCarried = (code: string) => (draft?.data.applicability_carried ?? []).filter(c => c !== code);
  const setAnswer = (code: string, value: boolean | null) => {
    const next = { ...(draft?.data.applicability ?? {}) };
    if (value == null) delete next[code];
    else next[code] = value;
    setData({ applicability: next, applicability_carried: withoutCarried(code) });
  };
  const confirmAnswer = (code: string) => setData({ applicability_carried: withoutCarried(code) });
  const confirmAllAnswers = () => setData({ applicability_carried: [] });

  // ───── ADR-0024 R-4b — import from the previous year ────────────────────
  const [carrying, setCarrying] = useState(false);
  const handleImportFromPrior = async () => {
    setCarrying(true);
    try {
      const r = await fsaCorporateFactsService.importFromPrior(engagementId);
      notificationService.toast.success(r.data?.message ?? 'Imported from the previous year.', 8000);
      await refetch();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not import from the previous year.');
    } finally {
      setCarrying(false);
    }
  };

  const addDividend = () => setData({
    dividends: [...(draft?.data.dividends ?? []), { local_id: generateLocalId(), kind: 'INTERIM' }],
  });
  const updateDividend = (id: string, patch: Partial<DividendEntry>) => setData({
    dividends: (draft?.data.dividends ?? []).map(d => (d.local_id === id ? { ...d, ...patch } : d)),
  });
  // ───── related-party transactions (Form AOC-2) ────────────────────────
  const rpts = draft?.data.related_party_transactions ?? [];
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<RelatedPartyImportResult | null>(null);
  const handleProposeImport = async () => {
    setImporting(true);
    try {
      const r = await fsaCorporateFactsService.proposeRelatedParties(engagementId);
      setImportResult(r.data ?? { rows: [], messages: ['Nothing returned.'] });
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not read the related-party note.');
    } finally {
      setImporting(false);
    }
  };
  // Adds the proposed rows (skipping a party + nature already in the list);
  // the auditor then completes duration, terms and approval date and saves.
  const acceptImport = () => {
    if (!importResult) return;
    const key = (p?: string | null, n?: string | null) => `${(p ?? '').trim().toLowerCase()}|${(n ?? '').trim().toLowerCase()}`;
    const existing = new Set(rpts.map(r => key(r.party, r.nature)));
    const additions: RelatedPartyTransaction[] = importResult.rows
      .filter(r => !existing.has(key(r.party, r.nature)))
      .map(r => ({
        local_id: generateLocalId(),
        party: r.party,
        relationship: r.relationship ?? undefined,
        nature: r.nature ?? undefined,
        value_cy: r.valueCy ?? null,
        value_py: r.valuePy ?? null,
        arms_length: true,
      }));
    setData({ related_party_transactions: [...rpts, ...additions] });
    notificationService.toast.success(
      additions.length > 0
        ? `${additions.length} transaction(s) added — review them and Save.`
        : 'All proposed transactions are already in the list.');
    setImportResult(null);
  };

  const removeDividend = (id: string) => setData({
    dividends: (draft?.data.dividends ?? []).filter(d => d.local_id !== id),
  });

  // ───── save ────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!draft) return;
    if ((draft.data.board_meetings ?? []).some(m => !m.date)) {
      notificationService.toast.error('Every board meeting needs a date.');
      return;
    }
    setIsSaving(true);
    try {
      // Forex (Annexure A) follows the Additional information rows (f)–(i).
      const grids = (addlInfo?.defs ?? []).flatMap(d => d.grids ?? []);
      const forex = grids.length ? forexFromGrids(grids, draft.data) : null;
      const toSave = forex ? { ...draft.data, forex: { earnings: forex.earnings, outgo: forex.outgo, source: 'note' as const } } : draft.data;
      // Only the changed sections — another screen's edits to other sections stay.
      await fsaCorporateFactsService.saveSections(engagementId, sectionSaveRequest(facts, toSave, {
        agmNumber: draft.agmNumber.trim() === '' ? null : Number(draft.agmNumber),
        agmDate: draft.agmDate || null,
        agmTime: draft.agmTime || null,
        agmVenue: draft.agmVenue || null,
      }));
      notificationService.toast.success('Corporate facts saved.');
      await refetch();
      onSaved?.();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Error saving corporate facts.');
    } finally {
      setIsSaving(false);
    }
  }, [draft, engagementId, facts, refetch, addlInfo, onSaved]);

  if (loading && !draft) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading corporate facts…</div>;
  }
  if (!draft) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Couldn't load corporate facts.</div>;
  }

  const answers = draft.data.applicability ?? {};
  const unanswered = (questions ?? []).filter(q =>
    typeof answers[q.code] !== 'boolean' && !derivedFromFacts(q.deriveFrom, draft.data)).length;
  const carriedSet = new Set(draft.data.applicability_carried ?? []);
  const toConfirm = (questions ?? []).filter(q => carriedSet.has(q.code)).length;
  // The directors list taken from the master on first open is not the
  // auditor's entry — it must not hide the import (which brings last year's list).
  const dirtyBeyondSeed = seededFromMaster
    ? JSON.stringify({ ...draft, data: { ...draft.data, directors: undefined } }) !== snapshot
    : isDirty;
  const canImport = facts?.hasPriorEngagement === true && !facts?.id && !dirtyBeyondSeed;

  // ───── section workspace ──────────────────────────────────────────────
  const masterChanged = drift != null && hasDrift(drift);
  const namedFacts = draft.data;
  const sectionNav: Array<{ key: FactsSection; label: string; badge: string | null; warn: boolean }> = [
    {
      key: 'company', label: 'Company & AGM',
      badge: namedFacts.nature_of_business?.trim() && draft.agmNumber && draft.agmDate ? '✓' : 'to fill',
      warn: !(namedFacts.nature_of_business?.trim() && draft.agmNumber && draft.agmDate),
    },
    { key: 'directors', label: 'Directors', badge: String(roster.length), warn: masterChanged || roster.length === 0 },
    { key: 'meetings', label: 'Board meetings & attendance', badge: String(meetings.length), warn: false },
    { key: 'changes', label: 'Changes in directors', badge: (namedFacts.director_changes ?? []).length ? String((namedFacts.director_changes ?? []).length) : null, warn: false },
    {
      key: 'members', label: 'Share capital & members',
      badge: (namedFacts.share_classes ?? []).length ? `${closingMembers(namedFacts.members_opening ?? [], namedFacts.share_movements ?? []).length}` : 'to fill',
      warn: (namedFacts.share_classes ?? []).length === 0,
    },
    {
      key: 'questions', label: "Board's Report questions",
      badge: questions == null || questions.length === 0 ? null : unanswered > 0 ? `${unanswered} left` : toConfirm > 0 ? `${toConfirm} to confirm` : '✓',
      warn: unanswered > 0 || toConfirm > 0,
    },
    {
      key: 'related', label: 'Related parties',
      badge: `${(namedFacts.related_parties ?? []).length} / ${(namedFacts.related_party_transactions ?? []).length}`, warn: false,
    },
    {
      key: 'additional', label: 'Additional information',
      badge: Object.keys(namedFacts.note_grids ?? {}).length ? '✓' : null, warn: false,
    },
    { key: 'dividends', label: 'Dividends', badge: (namedFacts.dividends ?? []).length ? String((namedFacts.dividends ?? []).length) : null, warn: false },
    { key: 'other', label: 'Other disclosures', badge: namedFacts.posh || namedFacts.employees ? '✓' : null, warn: false },
  ];
  const dirtyKeys = new Set(Object.keys(changedSections(snapshotDraft?.data ?? {}, draft.data)));
  const agmDirty = snapshotDraft != null && (['agmNumber', 'agmDate', 'agmTime', 'agmVenue'] as const)
    .some(k => (snapshotDraft as any)[k] !== (draft as any)[k]);
  const dirtySections = sectionNav.filter(sec => SECTION_KEYS[sec.key].some(k => dirtyKeys.has(k)) || (sec.key === 'company' && agmDirty));

  return (
    <div style={shellStyle}>
      <div style={topBarStyle}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)' }}>Corporate Facts</h3>
          <div style={subtleStyle}>
            The year's data ({formatDisplayDate(fyStart)} – {formatDisplayDate(fyEnd)}), entered once and read by the Board's Report,
            the notes, the AGM Notice and the other statutory documents.
            {facts?.updatedByName ? ` Last saved by ${facts.updatedByName}${facts.updatedAt ? ` · ${new Date(facts.updatedAt).toLocaleString()}` : ''}.` : ''}
          </div>
        </div>
      </div>
        {/* ───── ADR-0024 R-4b — carry forward ───── */}
        {canImport && (
          <div style={{ ...importBoxStyle, marginTop: 0, margin: '0 1rem 0.5rem', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <strong>Start from last year's facts?</strong> Carries the directors list (those still in office, in the same order), the AGM number (+1), venue, Notice details, related-party
              transactions (last year's values become the previous-year column) and the applicability answers — each answer
              marked for you to confirm. Meetings, attendance, director changes, dividends and resolutions start empty.
            </div>
            <button className="btn btn-primary" style={{ ...smallBtnStyle, flexShrink: 0 }} onClick={handleImportFromPrior} disabled={carrying}>
              <IconSystem.download size={14} /> {carrying ? 'Importing…' : 'Import from previous year'}
            </button>
          </div>
        )}
        {draft.data.carried_from && toConfirm > 0 && (
          <div style={{ ...carriedBannerStyle, margin: '0 1rem 0.5rem' }}>
            Imported from the previous year — {toConfirm} applicability answer{toConfirm === 1 ? '' : 's'} still to confirm (Board's Report questions).
          </div>
        )}
      <div style={bodyRowStyle}>
        <nav style={navStyle} aria-label="Corporate Facts sections">
          {sectionNav.map(sec => {
            const on = sec.key === section;
            const dirty = dirtySections.some(d => d.key === sec.key);
            return (
              <button
                key={sec.key}
                type="button"
                onClick={() => chooseSection(sec.key)}
                aria-current={on ? 'page' : undefined}
                style={{ ...navItemStyle, background: on ? 'var(--bg-primary)' : 'transparent', fontWeight: on ? 700 : 500, boxShadow: on ? 'inset 3px 0 0 var(--primary-color, #1d4ed8)' : 'none' }}
              >
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sec.label}</span>
                {dirty && <span style={dirtyDotStyle} title="Unsaved changes" />}
                {sec.badge && <span style={sec.warn ? warnPillStyle : navBadgeStyle}>{sec.badge}</span>}
              </button>
            );
          })}
        </nav>
        <main style={mainStyle}>
          {section === 'company' && (
            <>
              {/* ───── Company (ADR-0024 N-1 — Note 1) ───── */}
              <div style={cardStyle}>
                <h4 style={cardTitleStyle}>Company</h4>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Nature of business</label>
                  <textarea className="form-textarea" rows={2} maxLength={500}
                    value={draft.data.nature_of_business ?? ''}
                    onChange={e => setData({ nature_of_business: e.target.value })}
                    placeholder="Completes “The Company is engaged in …”, e.g. the manufacture and sale of …" />
                  <div style={subtleStyle}>
                    Printed in Note 1 (Company Information). Name, CIN, registered office, date of incorporation and currency
                    come from the client master; the approval date from the signature panel. Carried to next year.
                  </div>
                </div>
              </div>
              {/* ───── AGM ───── */}
              <div style={cardStyle}>
                <h4 style={cardTitleStyle}>Annual General Meeting</h4>
                <div className="form-grid-3">
                  <div className="form-group">
                    <label className="form-label">AGM number</label>
                    <input
                      type="number" min={1} className="form-input"
                      value={draft.agmNumber}
                      onChange={e => setDraft({ ...draft, agmNumber: e.target.value })}
                      placeholder="e.g. 10 for the 10th AGM"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Date</label>
                    <input type="date" className="form-input" value={draft.agmDate}
                      onChange={e => setDraft({ ...draft, agmDate: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Time</label>
                    <input type="text" className="form-input" value={draft.agmTime} maxLength={40}
                      onChange={e => setDraft({ ...draft, agmTime: e.target.value })} placeholder="e.g. 11.00 A.M." />
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Venue</label>
                  <textarea className="form-textarea" rows={2} value={draft.agmVenue} maxLength={500}
                    onChange={e => setDraft({ ...draft, agmVenue: e.target.value })}
                    placeholder="Full venue as it should print on the Notice, proxy form and attendance slip" />
                </div>
              </div>
            </>
          )}
          {section === 'directors' && (
            <>
              {/* ───── Directors — the engagement's own list (ADR-0024) ───── */}
              <div style={cardStyle}>
                <div style={cardHeaderRowStyle}>
                  <h4 style={cardTitleStyle}>Directors <span style={countPillStyle}>{roster.length}</span></h4>
                  <button className="btn btn-secondary" onClick={syncFromMaster} style={smallBtnStyle}
                    disabled={!master || seededFromMaster || !drift || !hasDrift(drift)}
                    title="Take the master's current names, DINs, designations and dates; add directors new to the master at the end. Your order is kept.">
                    <IconSystem.refresh size={14} /> Sync from master
                  </button>
                </div>
                <div style={subtleStyle}>
                  Kept with this engagement, so later changes in the Director Signatories master do not alter this year. The order
                  here is the order used in the Board's Report (Directors and KMP, attendance), the pickers on this screen and in the
                  AGM Notice, and new signature panels.
                </div>
                {!clientId ? (
                  <div style={emptyRowStyle}>This engagement has no client — directors cannot be listed.</div>
                ) : (
                  <>
                    {seededFromMaster && (
                      <div style={{ ...carriedBannerStyle, margin: '8px 0' }}>
                        Taken from the Director Signatories master for this year, in its default order (appointment date, then name).
                        Arrange the order and <strong>Save</strong> to keep it with this engagement.
                      </div>
                    )}
                    {drift && hasDrift(drift) && (
                      <div style={importBoxStyle}>
                        <div>
                          <strong>The master has changed since this list was taken:</strong>{' '}
                          {[
                            drift.changed.length > 0 ? `${drift.changed.length} with different details` : null,
                            drift.added.length > 0 ? `${drift.added.length} new for the year (${drift.added.map(a => a.name).join(', ')})` : null,
                            drift.missing.length > 0 ? `${drift.missing.length} no longer in the master for the year (marked below; remove if appropriate)` : null,
                          ].filter(Boolean).join('; ')}.
                          {' '}Nothing changes until you use Sync from master and Save.
                        </div>
                      </div>
                    )}
                    {roster.length === 0 && (
                      <div style={emptyRowStyle}>
                        No directors listed. Add them in FSA → Director Signatories (with effective dates), then use Sync from master.
                      </div>
                    )}
                    {roster.map((d, i) => (
                      <div key={d.director_id} style={rowGrid('28px 1fr 230px auto')}>
                        <span style={{ ...subtleStyle, paddingTop: 2, textAlign: 'right' }}>{i + 1}.</span>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                            {d.name}
                            {missingIds.has(d.director_id.toLowerCase()) && (
                              <>{' '}<span style={warnPillStyle} title="Deleted from the master, or its dates no longer fall in this year">not in master for the year</span></>
                            )}
                          </div>
                          <div style={subtleStyle}>{d.din ? `DIN ${d.din} · ` : ''}{d.designation ?? ''}</div>
                        </div>
                        <div style={{ ...subtleStyle, paddingTop: 2 }}>
                          {d.appointed ? `Appointed ${formatDisplayDate(d.appointed)}` : 'Appointment date not set'}
                          {d.ceased ? ` · Ceased ${formatDisplayDate(d.ceased)}` : ''}
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button type="button" className="btn btn-secondary" style={iconBtnStyle} disabled={i === 0}
                            onClick={() => moveDirector(i, -1)} title="Move up">
                            <IconSystem.chevronUp size={14} />
                          </button>
                          <button type="button" className="btn btn-secondary" style={iconBtnStyle} disabled={i === roster.length - 1}
                            onClick={() => moveDirector(i, 1)} title="Move down">
                            <IconSystem.chevronDown size={14} />
                          </button>
                          <button type="button" className="btn btn-secondary" style={iconBtnStyle}
                            onClick={() => removeDirector(d.director_id)} title="Remove from this engagement's list (and their attendance ticks)">
                            <IconSystem.trash size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </>
          )}
          {section === 'meetings' && (
            <>
              {/* ───── Board meetings ───── */}
              <div style={cardStyle}>
                <div style={cardHeaderRowStyle}>
                  <h4 style={cardTitleStyle}>Board Meetings <span style={countPillStyle}>{meetings.length}</span></h4>
                  <button className="btn btn-primary" onClick={addMeeting} style={smallBtnStyle}>
                    <IconSystem.add size={14} /> Add Meeting
                  </button>
                </div>
                {meetings.length === 0 && <div style={emptyRowStyle}>No board meetings recorded yet.</div>}
                {meetings.map(m => {
                  const outside = m.date && (m.date < fyStart || m.date > fyEnd);
                  return (
                    <div key={m.local_id} style={rowGrid('180px 1fr auto')}>
                      <input type="date" className="form-input" value={m.date}
                        onChange={e => updateMeeting(m.local_id, { date: e.target.value })} />
                      <div>
                        <input type="text" className="form-input" value={m.notes ?? ''} placeholder="Notes (optional)"
                          onChange={e => updateMeeting(m.local_id, { notes: e.target.value })} />
                        {outside && <div style={warnStyle}>Outside the financial year — check the date.</div>}
                      </div>
                      <button type="button" className="btn btn-secondary" style={iconBtnStyle}
                        onClick={() => removeMeeting(m.local_id)} title="Remove meeting (and its attendance)">
                        <IconSystem.trash size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
              {/* ───── Attendance ───── */}
              <div style={cardStyle}>
                <h4 style={cardTitleStyle}>Attendance at Board Meetings</h4>
                {!clientId ? (
                  <div style={emptyRowStyle}>This engagement has no client — directors cannot be listed.</div>
                ) : directors.length === 0 ? (
                  <div style={emptyRowStyle}>
                    No directors in office during the year in the Directors list above.
                  </div>
                ) : meetings.filter(m => m.date).length === 0 ? (
                  <div style={emptyRowStyle}>Add board meetings above to record attendance.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={gridTableStyle}>
                      <thead>
                        <tr>
                          <th style={{ ...gridThStyle, textAlign: 'left', minWidth: 220 }}>Director</th>
                          {meetings.filter(m => m.date).map(m => (
                            <th key={m.local_id} style={gridThStyle}>
                              <div>{formatDisplayDate(m.date)}</div>
                              <button type="button" style={linkBtnStyle} onClick={() => markAllPresent(m)}
                                title="Tick every director in office on this date">
                                all present
                              </button>
                            </th>
                          ))}
                          <th style={gridThStyle}>Attended</th>
                        </tr>
                      </thead>
                      <tbody>
                        {directors.map(d => {
                          const did = d.director_id;
                          const dated = meetings.filter(m => m.date);
                          const attended = dated.filter(m => attendanceSet.has(`${m.local_id}|${did}`)).length;
                          const eligible = dated.filter(m => inOffice(d, m.date)).length;
                          return (
                            <tr key={did}>
                              <td style={{ ...gridTdStyle, textAlign: 'left' }}>
                                <div style={{ fontWeight: 600 }}>{d.name}</div>
                                <div style={subtleStyle}>{d.din ? `DIN ${d.din} · ` : ''}{d.designation}</div>
                              </td>
                              {dated.map(m => {
                                const ok = inOffice(d, m.date);
                                return (
                                  <td key={m.local_id} style={gridTdStyle}>
                                    <input
                                      type="checkbox"
                                      checked={attendanceSet.has(`${m.local_id}|${did}`)}
                                      disabled={!ok}
                                      title={ok ? '' : 'Not in office on this date'}
                                      onChange={() => toggleAttendance(m.local_id, did)}
                                    />
                                  </td>
                                );
                              })}
                              <td style={gridTdStyle}>{attended} / {eligible}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
          {section === 'changes' && (
            <>
              {/* ───── Director changes ───── */}
              <div style={cardStyle}>
                <div style={cardHeaderRowStyle}>
                  <h4 style={cardTitleStyle}>Changes in Directors <span style={countPillStyle}>{(draft.data.director_changes ?? []).length}</span></h4>
                  <button className="btn btn-primary" onClick={addChange} style={smallBtnStyle}>
                    <IconSystem.add size={14} /> Add Change
                  </button>
                </div>
                <div style={subtleStyle}>During the year and up to the date of the report — feeds the report and, later, the AGM resolutions.</div>
                {(draft.data.director_changes ?? []).length === 0 && <div style={emptyRowStyle}>No changes recorded.</div>}
                {(draft.data.director_changes ?? []).map(c => (
                  <div key={c.local_id} style={rowGrid('220px 1fr 130px 150px auto')}>
                    <select className="form-input" value={c.kind}
                      onChange={e => updateChange(c.local_id, { kind: e.target.value as DirectorChangeKind })}>
                      {(Object.keys(CHANGE_KIND_LABEL) as DirectorChangeKind[]).map(k => (
                        <option key={k} value={k}>{CHANGE_KIND_LABEL[k]}</option>
                      ))}
                    </select>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <select
                        className="form-input"
                        value={c.director_id ?? ''}
                        onChange={e => {
                          const d = roster.find(x => x.director_id === e.target.value);
                          updateChange(c.local_id, d
                            ? { director_id: d.director_id, name: d.name, din: d.din ?? '', designation: d.designation ?? '' }
                            : { director_id: null });
                        }}
                        style={{ maxWidth: 200 }}
                        title="Pick from the Directors list above, or type the name for someone not in it"
                      >
                        <option value="">— not in list —</option>
                        {roster.map(d => <option key={d.director_id} value={d.director_id}>{d.name}</option>)}
                      </select>
                      <input type="text" className="form-input" value={c.name} placeholder="Name"
                        disabled={!!c.director_id}
                        onChange={e => updateChange(c.local_id, { name: e.target.value })} />
                    </div>
                    <input type="text" className="form-input" value={c.din ?? ''} placeholder="DIN" maxLength={20}
                      disabled={!!c.director_id}
                      onChange={e => updateChange(c.local_id, { din: e.target.value })} />
                    <input type="date" className="form-input" value={c.effective_date ?? ''}
                      onChange={e => updateChange(c.local_id, { effective_date: e.target.value })} />
                    <button type="button" className="btn btn-secondary" style={iconBtnStyle}
                      onClick={() => removeChange(c.local_id)} title="Remove">
                      <IconSystem.trash size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
          {section === 'members' && (
            <MembersEditor data={draft.data} onChange={setData} />
          )}
          {section === 'questions' && (
            <>
              {/* ───── Applicability ───── */}
              <div style={cardStyle}>
                <div style={cardHeaderRowStyle}>
                  <h4 style={cardTitleStyle}>Board's Report — Applicability</h4>
                  {questions && questions.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      {toConfirm > 0 && (
                        <>
                          <span style={carriedPillStyle}>{toConfirm} from previous year — confirm</span>
                          <button type="button" style={linkBtnStyle} onClick={confirmAllAnswers}
                            title="Keep every carried answer as it is for this year">
                            confirm all
                          </button>
                        </>
                      )}
                      <span style={unanswered > 0 ? warnPillStyle : okPillStyle}>
                        {unanswered > 0 ? `${unanswered} unanswered` : 'All answered'}
                      </span>
                    </div>
                  )}
                </div>
                <div style={subtleStyle}>
                  Each answer picks the "applies" or "not applicable" text of a report section. Unanswered questions use the
                  section's default. The report editor can still override any single section.
                </div>
                {questions == null ? (
                  <div style={emptyRowStyle}>Loading questions…</div>
                ) : questions.length === 0 ? (
                  <div style={emptyRowStyle}>The Board's Report for this engagement is not the sectioned version — no questions to answer.</div>
                ) : (
                  questions.map(q => {
                    const value = answers[q.code];
                    const carried = carriedSet.has(q.code);
                    return (
                      <div key={q.code} style={questionRowStyle}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                            {q.prompt}
                            {carried && (
                              <>
                                {' '}<span style={carriedPillStyle} title="Answer carried from the previous year — still true this year?">from previous year</span>
                                {' '}<button type="button" style={linkBtnStyle} onClick={() => confirmAnswer(q.code)}>confirm</button>
                              </>
                            )}
                          </div>
                          <div style={subtleStyle}>
                            {suggestions[q.code] && (
                              <div style={{ color: '#1e40af' }} title={suggestions[q.code].note ?? undefined}>
                                Suggested from the saved figures: <strong>{suggestions[q.code].answer ? 'Yes' : 'No'}</strong> — {suggestions[q.code].reason}
                                {typeof value === 'boolean' && value !== suggestions[q.code].answer && ' · differs from your answer'}
                              </div>
                            )}
                            Drives: {q.sectionTitles.join(', ')}
                            {typeof value !== 'boolean' && (derivedFromFacts(q.deriveFrom, draft.data)
                              ? ' · treated as Yes — entries are recorded below'
                              : q.defaultAnswer != null ? ` · default: ${q.defaultAnswer ? 'Yes' : 'No'}` : '')}
                          </div>
                        </div>
                        <div style={segmentedStyle}>
                          <SegButton active={value === true} onClick={() => setAnswer(q.code, true)}>Yes</SegButton>
                          <SegButton active={value === false} onClick={() => setAnswer(q.code, false)}>No</SegButton>
                          <SegButton active={typeof value !== 'boolean'} onClick={() => setAnswer(q.code, null)} muted>—</SegButton>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
          {section === 'related' && (
            <>
              {/* ───── ADR-0024 N-3 — Related Party Register (AS 18 + Form AOC-2) ───── */}
              <RelatedPartyRegisterEditor
                data={draft.data}
                onChange={setData}
                directors={roster}
                transactionsToolbar={(
                  <button className="btn btn-secondary" onClick={handleProposeImport} disabled={importing} style={smallBtnStyle}
                    title="Read the transactions table of the Additional Information note's Related Party Disclosures section">
                    {importing ? 'Reading…' : 'Import from Related Party note'}
                  </button>
                )}
                transactionsBanner={(
                  <>
                    {importResult && (
                      <div style={importBoxStyle}>
                        {importResult.messages.map((m, i) => <div key={i}>{m}</div>)}
                        {importResult.rows.length > 0 && (
                          <table style={{ ...gridTableStyle, margin: '6px 0' }}>
                            <thead><tr>
                              <th style={{ ...gridThStyle, textAlign: 'left' }}>Party</th>
                              <th style={{ ...gridThStyle, textAlign: 'left' }}>Relationship</th>
                              <th style={{ ...gridThStyle, textAlign: 'left' }}>Nature</th>
                              <th style={gridThStyle}>Current year</th>
                              <th style={gridThStyle}>Previous year</th>
                            </tr></thead>
                            <tbody>
                              {importResult.rows.map((r, i) => (
                                <tr key={i}>
                                  <td style={{ ...gridTdStyle, textAlign: 'left' }}>{r.party}</td>
                                  <td style={{ ...gridTdStyle, textAlign: 'left' }}>{r.relationship ?? '—'}</td>
                                  <td style={{ ...gridTdStyle, textAlign: 'left' }}>{r.nature ?? '—'}</td>
                                  <td style={{ ...gridTdStyle, textAlign: 'right' }}>{r.valueCy ?? '—'}</td>
                                  <td style={{ ...gridTdStyle, textAlign: 'right' }}>{r.valuePy ?? '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button className="btn btn-secondary" style={smallBtnStyle} onClick={() => setImportResult(null)}>Cancel</button>
                          {importResult.rows.length > 0 && (
                            <button className="btn btn-primary" style={smallBtnStyle} onClick={acceptImport}>Add these to the list</button>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              />
            </>
          )}
          {section === 'additional' && (
            <div style={cardStyle}>
              <h4 style={cardTitleStyle}>Additional information (Schedule III)</h4>
              <div style={subtleStyle}>
                Printed as the tables of the Additional Information note. Foreign exchange rows (f)–(i) also feed Annexure A of the
                Board's Report; the analytical ratios' inputs and reasons for variance are entered here too.
              </div>
              {addlInfo == null ? (
                <div style={emptyRowStyle}>Loading…</div>
              ) : addlInfo.defs.length === 0 ? (
                <div style={emptyRowStyle}>
                  This engagement's Additional Information note is not the generated version — open the note and use
                  "Use generated version" to enter these figures here.
                </div>
              ) : (
                <NoteGridsEditor
                  defs={addlInfo.defs}
                  data={draft.data}
                  update={fn => setDraft(prev => (prev ? { ...prev, data: fn(prev.data) } : prev))}
                  liveFields={addlInfo.live?.fields}
                  liveTables={addlInfo.live?.tables}
                  liveRatioOpenings={addlInfo.live?.ratioOpenings}
                  hasPriorEngagement={facts?.hasPriorEngagement === true}
                  refreshing={refreshingOpenings}
                  refreshBlockedReason={isDirty ? 'Save your changes first.' : null}
                  onRefreshOpenings={handleRefreshOpenings}
                />
              )}
            </div>
          )}
          {section === 'dividends' && (
            <>
              {/* ───── Dividends ───── */}
              <div style={cardStyle}>
                <div style={cardHeaderRowStyle}>
                  <h4 style={cardTitleStyle}>Dividends <span style={countPillStyle}>{(draft.data.dividends ?? []).length}</span></h4>
                  <button className="btn btn-primary" onClick={addDividend} style={smallBtnStyle}>
                    <IconSystem.add size={14} /> Add Dividend
                  </button>
                </div>
                {(draft.data.dividends ?? []).length === 0 && <div style={emptyRowStyle}>No dividend declared or recommended.</div>}
                {(draft.data.dividends ?? []).length > 0 && (
                  <div style={{ ...rowGrid('140px 170px 120px 140px 160px auto'), ...subtleStyle, borderTop: 'none' }}>
                    <span>Type</span><span>Board meeting date</span><span>Rate %</span><span>Per share</span><span>Total amount</span><span />
                  </div>
                )}
                {(draft.data.dividends ?? []).map(d => (
                  <div key={d.local_id} style={rowGrid('140px 170px 120px 140px 160px auto')}>
                    <select className="form-input" value={d.kind}
                      onChange={e => updateDividend(d.local_id, { kind: e.target.value as DividendEntry['kind'] })}>
                      <option value="INTERIM">Interim</option>
                      <option value="FINAL">Final (recommended)</option>
                    </select>
                    <input type="date" className="form-input" value={d.board_meeting_date ?? ''}
                      onChange={e => updateDividend(d.local_id, { board_meeting_date: e.target.value })} />
                    <input type="number" className="form-input" value={d.rate_percent ?? ''}
                      onChange={e => updateDividend(d.local_id, { rate_percent: toNumberOrNull(e.target.value) })} />
                    <input type="number" className="form-input" value={d.per_share_amount ?? ''}
                      onChange={e => updateDividend(d.local_id, { per_share_amount: toNumberOrNull(e.target.value) })} />
                    <input type="number" className="form-input" value={d.total_amount ?? ''}
                      onChange={e => updateDividend(d.local_id, { total_amount: toNumberOrNull(e.target.value) })} />
                    <button type="button" className="btn btn-secondary" style={iconBtnStyle}
                      onClick={() => removeDividend(d.local_id)} title="Remove">
                      <IconSystem.trash size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
          {section === 'other' && (
            <>
              {/* ───── Foreign exchange (Annexure A) ───── */}
              <div style={cardStyle}>
                <h4 style={cardTitleStyle}>Foreign Exchange Earnings and Outgo</h4>
                {draft.data.forex?.source === 'note' ? (
                  // ADR-0024 N-4 — one source: the Additional Information note's (f)–(i) rows.
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                    Earnings <strong>{draft.data.forex.earnings ?? 0}</strong> · Outgo <strong>{draft.data.forex.outgo ?? 0}</strong>
                    <div style={subtleStyle}>
                      Set from Additional information, rows (f)–(i) —{' '}
                      <button type="button" style={linkBtnStyle} onClick={() => chooseSection('additional')}>change them there</button>.
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="form-grid-3">
                      <NumberField label="Foreign exchange earnings" value={draft.data.forex?.earnings}
                        onChange={v => setData({ forex: { ...(draft.data.forex ?? {}), earnings: v } })} />
                      <NumberField label="Foreign exchange outgo" value={draft.data.forex?.outgo}
                        onChange={v => setData({ forex: { ...(draft.data.forex ?? {}), outgo: v } })} />
                    </div>
                    <div style={subtleStyle}>
                      For Annexure A (C). Enter 0 for Nil; leave blank until known. Once rows (f)–(i) are filled in
                      Additional information, they become the source.
                    </div>
                  </>
                )}
              </div>
              {/* ───── POSH + Employees ───── */}
              <div style={cardStyle}>
                <h4 style={cardTitleStyle}>POSH Complaints and Employee Strength</h4>
                <div className="form-grid-3">
                  <NumberField label="Complaints received" value={draft.data.posh?.received}
                    onChange={v => setData({ posh: { ...(draft.data.posh ?? {}), received: v } })} />
                  <NumberField label="Complaints disposed of" value={draft.data.posh?.disposed}
                    onChange={v => setData({ posh: { ...(draft.data.posh ?? {}), disposed: v } })} />
                  <NumberField label="Pending > 90 days" value={draft.data.posh?.pending_90}
                    onChange={v => setData({ posh: { ...(draft.data.posh ?? {}), pending_90: v } })} />
                  <NumberField label="Employees — female" value={draft.data.employees?.female}
                    onChange={v => setData({ employees: { ...(draft.data.employees ?? {}), female: v } })} />
                  <NumberField label="Employees — male" value={draft.data.employees?.male}
                    onChange={v => setData({ employees: { ...(draft.data.employees ?? {}), male: v } })} />
                  <NumberField label="Employees — transgender" value={draft.data.employees?.transgender}
                    onChange={v => setData({ employees: { ...(draft.data.employees ?? {}), transgender: v } })} />
                </div>
                <div style={subtleStyle}>Employee counts are as on the financial year end.</div>
              </div>
            </>
          )}
        </main>
      </div>
      <div style={saveBarStyle}>
        <span style={{ flex: 1, fontSize: '0.8rem', color: dirtySections.length ? '#92400e' : 'var(--text-secondary)' }}>
          {dirtySections.length
            ? <>● Unsaved changes — {dirtySections.map(d => d.label).join(', ')}</>
            : 'All changes saved.'}
        </span>
        <button className="btn btn-secondary" onClick={handleDiscard} disabled={isSaving || !isDirty}>Discard</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={isSaving || !isDirty}>
          {isSaving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
};

const NumberField: React.FC<{
  label: string;
  value: number | null | undefined;
  onChange: (v: number | null) => void;
}> = ({ label, value, onChange }) => (
  <div className="form-group">
    <label className="form-label">{label}</label>
    <input type="number" min={0} className="form-input" value={value ?? ''}
      onChange={e => onChange(toNumberOrNull(e.target.value))} />
  </div>
);

const SegButton: React.FC<{ active: boolean; onClick: () => void; muted?: boolean; children: React.ReactNode }> = ({
  active, onClick, muted, children,
}) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: '3px 12px',
      border: 'none',
      fontSize: '0.78rem',
      fontWeight: 600,
      cursor: 'pointer',
      background: active ? (muted ? '#64748b' : 'var(--primary-color, #2563eb)') : 'transparent',
      color: active ? 'white' : 'var(--text-secondary)',
    }}
  >
    {children}
  </button>
);

// ───── styles ──────────────────────────────────────────────────────────────

const shellStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 };
const topBarStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 12, padding: '0.75rem 1.25rem 0.5rem', flexShrink: 0,
};
const bodyRowStyle: React.CSSProperties = { display: 'flex', flex: 1, minHeight: 0, borderTop: '1px solid var(--border-color)' };
const navStyle: React.CSSProperties = {
  width: 250, flexShrink: 0, overflowY: 'auto', padding: '8px 6px', background: 'var(--bg-secondary)',
  borderRight: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: 2,
};
const navItemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '9px 10px', border: 'none', borderRadius: 6,
  cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-primary)', textAlign: 'left',
};
const navBadgeStyle: React.CSSProperties = {
  fontSize: '0.68rem', fontWeight: 700, padding: '1px 7px', borderRadius: 999, background: 'var(--bg-primary)',
  color: 'var(--text-secondary)', border: '1px solid var(--border-color)', whiteSpace: 'nowrap',
};
const dirtyDotStyle: React.CSSProperties = { width: 7, height: 7, borderRadius: 999, background: '#d97706', flexShrink: 0 };
const mainStyle: React.CSSProperties = { flex: 1, minWidth: 0, overflowY: 'auto', padding: '1rem 1.25rem' };
const saveBarStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 1.25rem', borderTop: '1px solid var(--border-color)',
  background: 'var(--bg-primary)', flexShrink: 0,
};
const cardStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)', borderRadius: 8, padding: '1rem', marginBottom: '1rem',
};
const cardHeaderRowStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', gap: '0.5rem',
};
const cardTitleStyle: React.CSSProperties = {
  margin: '0 0 0.5rem 0', fontSize: '0.9375rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8,
};
const subtleStyle: React.CSSProperties = { fontSize: '0.75rem', color: 'var(--text-secondary)' };
const emptyRowStyle: React.CSSProperties = { fontSize: '0.8125rem', color: 'var(--text-secondary)', padding: '0.5rem' };
const warnStyle: React.CSSProperties = { fontSize: '0.72rem', color: '#92400e', marginTop: 2 };
const smallBtnStyle: React.CSSProperties = { fontSize: '0.8125rem', padding: '0.375rem 0.75rem' };
const iconBtnStyle: React.CSSProperties = { padding: '0.375rem 0.5rem' };
const countPillStyle: React.CSSProperties = {
  fontSize: '0.7rem', fontWeight: 600, padding: '0 7px', borderRadius: 999,
  background: 'var(--bg-primary)', color: 'var(--text-secondary)',
};
const pillBase: React.CSSProperties = { fontSize: '0.7rem', fontWeight: 700, padding: '1px 8px', borderRadius: 999 };
const warnPillStyle: React.CSSProperties = { ...pillBase, background: '#fef3c7', color: '#92400e' };
const okPillStyle: React.CSSProperties = { ...pillBase, background: '#dcfce7', color: '#166534' };
const carriedPillStyle: React.CSSProperties = { ...pillBase, background: '#ede9fe', color: '#5b21b6', fontWeight: 600 };
const carriedBannerStyle: React.CSSProperties = {
  padding: '8px 12px', borderRadius: 6, background: '#f5f3ff', border: '1px solid #ddd6fe', color: '#4c1d95', fontSize: '0.8rem',
};
const importBoxStyle: React.CSSProperties = {
  margin: '8px 0', padding: '8px 10px', borderRadius: 6,
  background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e3a8a', fontSize: '0.78rem',
  display: 'flex', flexDirection: 'column', gap: 4,
};
const rowGrid = (cols: string): React.CSSProperties => ({
  display: 'grid', gridTemplateColumns: cols, gap: '0.5rem', alignItems: 'flex-start',
  padding: '0.5rem 0', borderTop: '1px solid var(--border-color)',
});
const gridTableStyle: React.CSSProperties = { borderCollapse: 'collapse', width: '100%', fontSize: '0.8125rem' };
const gridThStyle: React.CSSProperties = {
  padding: '6px 8px', borderBottom: '1px solid var(--border-color)', textAlign: 'center',
  fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap', fontSize: '0.75rem',
};
const gridTdStyle: React.CSSProperties = {
  padding: '6px 8px', borderBottom: '1px solid var(--border-color)', textAlign: 'center', color: 'var(--text-primary)',
};
const linkBtnStyle: React.CSSProperties = {
  border: 'none', background: 'transparent', color: 'var(--primary-color, #2563eb)', cursor: 'pointer',
  fontSize: '0.68rem', padding: 0, fontWeight: 500,
};
const questionRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0', borderTop: '1px solid var(--border-color)',
};
const segmentedStyle: React.CSSProperties = {
  display: 'inline-flex', border: '1px solid var(--form-border-color)', borderRadius: 6, overflow: 'hidden', flexShrink: 0,
};

export default FsaCorporateFactsPane;
